import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Typography, TextField, IconButton, Alert, Paper, Avatar, Fade, useMediaQuery, useTheme } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MinimizeIcon from '@mui/icons-material/Minimize';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { demoChat, uploadImageToConversation } from '../services/api';
import ReactMarkdown from 'react-markdown';
import config from '../config';
import ErrorBoundary from '../components/ErrorBoundary';

// Message interface for chat
interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  image?: string; // Optional image payload (base64 data URI)
}

// View mode type
type ViewMode = 'fullscreen' | 'corner';

// Chat message bubble component
const MessageBubble = ({
  message,
  viewMode,
  isMobile = false,
  chatbotIcon = '🤖',
  primaryColor = '#2196f3',
  assistantName = ''
}: {
  message: ChatMessage;
  viewMode: ViewMode;
  isMobile?: boolean;
  chatbotIcon?: string;
  primaryColor?: string;
  assistantName?: string;
}) => {
  const isUser = message.sender === 'user';
  const isCornerMode = viewMode === 'corner';
  const avatarSize = isCornerMode ? 28 : (isMobile ? 30 : 32);
  const hasImage = !!message.image;
  
  return (
    <Box
      className="message-bubble"
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: isCornerMode ? 1.5 : (isMobile ? 1.5 : 2),
        alignItems: 'flex-start',
        gap: isMobile ? 0.5 : 1,
        px: isMobile ? 1 : 0
      }}
    >
      {!isUser && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Avatar sx={{ bgcolor: chatbotIcon.startsWith('data:image/') ? 'transparent' : primaryColor, width: avatarSize, height: avatarSize }}>
            {chatbotIcon.startsWith('data:image/') ? (
              <img
                src={chatbotIcon}
                alt="Chatbot"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '50%'
                }}
              />
            ) : (
              <span style={{ fontSize: avatarSize * 0.6 }}>{chatbotIcon}</span>
            )}
          </Avatar>
          {assistantName && (
            <Typography
              variant="caption"
              sx={{
                fontSize: isCornerMode ? '0.65rem' : (isMobile ? '0.7rem' : '0.75rem'),
                color: 'text.secondary',
                textAlign: 'center',
                maxWidth: avatarSize + 20,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {assistantName}
            </Typography>
          )}
        </Box>
      )}
      
      <Paper
        sx={{
          maxWidth: isMobile ? '85%' : (isCornerMode ? '85%' : '70%'),
          minWidth: isCornerMode ? '80px' : '100px',
          p: isMobile ? 1.5 : (isCornerMode ? 1.5 : 2),
          bgcolor: isUser ? primaryColor : '#f5f5f5',
          color: isUser ? 'white' : 'black',
          borderRadius: isUser ? 
            (isCornerMode || isMobile ? '12px 12px 4px 12px' : '18px 18px 4px 18px') :
            (isCornerMode || isMobile ? '12px 12px 12px 4px' : '18px 18px 18px 4px'),
          wordBreak: 'break-word',
          fontSize: isMobile ? '0.9rem' : (isCornerMode ? '0.9rem' : '1rem'),
          // Mobile-specific optimizations for better touch experience
          ...(isMobile && {
            // Optimize for mobile performance
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
            // Better text rendering on mobile
            textRendering: 'optimizeLegibility',
            WebkitFontSmoothing: 'antialiased',
            // Improve tap performance
            touchAction: 'manipulation',
          })
        }}
      >
        {hasImage && (
          <Box sx={{ mb: message.content ? 1 : 0 }}>
            <img
              src={message.image}
              alt="AI generated"
              style={{
                maxWidth: isMobile ? '240px' : '320px',
                width: '100%',
                borderRadius: 8,
                display: 'block',
              }}
            />
          </Box>
        )}
        {message.content && (
          isUser ? (
            <Typography variant="body1" sx={{ fontSize: 'inherit' }}>{message.content}</Typography>
          ) : (
            <Box component="div" sx={{ 
              '& p': { margin: 0, fontSize: 'inherit' }, 
              '& p:not(:last-child)': { marginBottom: 1 }, 
              '& ul, & ol': { margin: 0, paddingLeft: '1.2em', fontSize: 'inherit' }, 
              '& pre': { margin: 0, bgcolor: 'rgba(0,0,0,0.1)', p: 1, borderRadius: 1, fontSize: '0.85em' },
              '& a': {
                color: primaryColor,
                textDecoration: 'underline',
                '&:hover': {
                  textDecoration: 'none',
                  opacity: 0.8
                }
              }
            }}>
              <ReactMarkdown
                components={{
                  a: ({ href, children, ...props }) => (
                    <a 
                      href={href} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      {...props}
                    >
                      {children}
                    </a>
                  )
                }}
              >
                {message.content}
              </ReactMarkdown>
            </Box>
          )
        )}
      </Paper>
      
      {isUser && (
        <Avatar sx={{ bgcolor: '#666', width: avatarSize, height: avatarSize }}>
          <PersonIcon fontSize="small" />
        </Avatar>
      )}
    </Box>
  );
};

// Corner Chat Window Component (external to prevent re-creation)
interface CornerChatWindowProps {
  viewMode: ViewMode;
  error: string | null;
  messages: ChatMessage[];
  inputMessage: string;
  loading: boolean;
  chatContainerRef: React.RefObject<HTMLDivElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  toggleViewMode: () => void;
  setError: (error: string | null) => void;
  setInputMessage: (message: string) => void;
  handleSendMessage: () => void;
  handleKeyPress: (e: React.KeyboardEvent) => void;
  handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isMobile: boolean;
  isSmartviewEnabled: boolean;
  uploadedImageBase64: string | null;
  widgetConfig: {
    aiTitle?: string;
    assistantName?: string;
    chatbotIcon?: string;
    theme?: {
      primaryColor?: string;
    };
  } | null;
}

const CornerChatWindow = React.memo(({
  viewMode,
  error,
  messages,
  inputMessage,
  loading,
  chatContainerRef,
  messagesEndRef,
  toggleViewMode,
  setError,
  setInputMessage,
  handleSendMessage,
  handleKeyPress,
  handleImageUpload,
  isMobile,
  isSmartviewEnabled,
  uploadedImageBase64,
  widgetConfig
}: CornerChatWindowProps) => {
  return (
    <Fade in={viewMode === 'corner'} timeout={300}>
    <Paper
      sx={{
        position: 'fixed',
        bottom: { xs: 10, sm: 20 },
        right: { xs: 10, sm: 20 },
        width: { xs: 'calc(100vw - 20px)', sm: 350 },
        height: { xs: 'calc(100vh - 20px)', sm: 500 },
        maxWidth: '350px',
        maxHeight: '500px',
        zIndex: 1000,
        borderRadius: 2,
        boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Corner Chat Header */}
      <Box sx={{
        p: 2,
        bgcolor: widgetConfig?.theme?.primaryColor || '#2196f3',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: (widgetConfig?.chatbotIcon || '').startsWith('data:image/') ? 'transparent' : 'rgba(255,255,255,0.2)', width: 32, height: 32 }}>
            {(widgetConfig?.chatbotIcon || '').startsWith('data:image/') ? (
              <img
                src={widgetConfig?.chatbotIcon}
                alt="Chatbot"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '50%'
                }}
              />
            ) : (
              <span style={{ fontSize: '16px' }}>{widgetConfig?.chatbotIcon || '🤖'}</span>
            )}
          </Avatar>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            {widgetConfig?.aiTitle || 'AI Demo Chat'}
          </Typography>
        </Box>
        <Box>
          <IconButton
            size="small"
            onClick={toggleViewMode}
            sx={{ color: 'white' }}
            title="Switch to fullscreen"
          >
            <OpenInFullIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ m: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Messages Area */}
      <Box
        ref={chatContainerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fafafa'
        }}
      >
        {(!messages || messages.length === 0) && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            textAlign: 'center'
          }}>
            <Box>
              <ChatBubbleOutlineIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Start a conversation
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Send a message to begin
              </Typography>
            </Box>
          </Box>
        )}

        {messages && messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            viewMode={viewMode}
            isMobile={isMobile}
            chatbotIcon={widgetConfig?.chatbotIcon || '🤖'}
            primaryColor={widgetConfig?.theme?.primaryColor || '#2196f3'}
            assistantName={widgetConfig?.assistantName || ''}
          />
        ))}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2, alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: (widgetConfig?.chatbotIcon || '').startsWith('data:image/') ? 'transparent' : (widgetConfig?.theme?.primaryColor || '#2196f3'), width: 28, height: 28 }}>
              {(widgetConfig?.chatbotIcon || '').startsWith('data:image/') ? (
                <img
                  src={widgetConfig?.chatbotIcon}
                  alt="Chatbot"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '50%'
                  }}
                />
              ) : (
                <span style={{ fontSize: '14px' }}>{widgetConfig?.chatbotIcon || '🤖'}</span>
              )}
            </Avatar>
            <Paper sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: '12px 12px 12px 4px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      bgcolor: '#999',
                      borderRadius: '50%',
                      animation: 'typing-dots 1.4s infinite ease-in-out',
                      '&:nth-of-type(1)': { animationDelay: '0s' },
                      '&:nth-of-type(2)': { animationDelay: '0.2s' },
                      '&:nth-of-type(3)': { animationDelay: '0.4s' },
                      '@keyframes typing-dots': {
                        '0%, 80%, 100%': { transform: 'scale(0)' },
                        '40%': { transform: 'scale(1)' }
                      }
                    }}
                  />
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      bgcolor: '#999',
                      borderRadius: '50%',
                      animation: 'typing-dots 1.4s infinite ease-in-out',
                      animationDelay: '0.2s',
                      '@keyframes typing-dots': {
                        '0%, 80%, 100%': { transform: 'scale(0)' },
                        '40%': { transform: 'scale(1)' }
                      }
                    }}
                  />
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      bgcolor: '#999',
                      borderRadius: '50%',
                      animation: 'typing-dots 1.4s infinite ease-in-out',
                      animationDelay: '0.4s',
                      '@keyframes typing-dots': {
                        '0%, 80%, 100%': { transform: 'scale(0)' },
                        '40%': { transform: 'scale(1)' }
                      }
                    }}
                  />
                </Box>
              </Box>
            </Paper>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box sx={{
        p: 2,
        bgcolor: 'white',
        borderTop: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          {isSmartviewEnabled && (
            <label htmlFor="corner-image-upload" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <IconButton component="span" sx={{ color: '#666', '&:hover': { bgcolor: '#f0f0f0' } }} title="Upload image">
                <span style={{ fontSize: '18px' }}>📎</span>
              </IconButton>
              <input
                id="corner-image-upload"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />
            </label>
          )}
          <TextField
            multiline
            maxRows={3}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Type a message..."
            variant="outlined"
            fullWidth
            disabled={loading}
            onKeyPress={handleKeyPress}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '16px',
                bgcolor: '#f8f9fa'
              }
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={loading || !inputMessage.trim()}
            sx={{
              bgcolor: widgetConfig?.theme?.primaryColor || '#2196f3',
              color: 'white',
              '&:hover': { bgcolor: widgetConfig?.theme?.primaryColor || '#2196f3', filter: 'brightness(0.9)' },
              '&:disabled': { bgcolor: '#e0e0e0', color: '#9e9e9e' }
            }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
        {uploadedImageBase64 && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <img src={uploadedImageBase64} alt="Uploaded" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: '4px' }} />
            <Typography variant="caption">Image attached</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  </Fade>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  return (
    prevProps.viewMode === nextProps.viewMode &&
    prevProps.error === nextProps.error &&
    prevProps.inputMessage === nextProps.inputMessage &&
    prevProps.loading === nextProps.loading &&
    prevProps.isMobile === nextProps.isMobile &&
    prevProps.messages?.length === nextProps.messages?.length &&
    prevProps.messages?.every((msg, index) => msg.id === nextProps.messages?.[index]?.id)
  );
});

// Add display name for better debugging during hot reloads
CornerChatWindow.displayName = 'CornerChatWindow';

const Demo = () => {
  const { aiId } = useParams<{ aiId: string }>();
  const [searchParams] = useSearchParams();
  const collectionId = searchParams.get('collectionId');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Chat state - array of messages (with default fallback)
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId] = useState(() => `demo-${Date.now()}`);
  const [conversationId, setConversationId] = useState<string | null>(null);

  // Widget configuration state
  const [widgetConfig, setWidgetConfig] = useState<{
    aiTitle?: string;
    assistantName?: string;
    chatbotIcon?: string;
    instruction?: string;
    theme?: {
      primaryColor?: string;
    };
  } | null>(null);

  // Track if config is loaded to prevent showing defaults before config loads
  const [configLoaded, setConfigLoaded] = useState(false);

  // Smartview image upload state
  const [isSmartviewEnabled, setIsSmartviewEnabled] = useState(false);
  const [uploadedImageBase64, setUploadedImageBase64] = useState<string | null>(null);

  // View mode state - force fullscreen on mobile, otherwise use saved preference
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (isMobile) return 'fullscreen';
    const saved = localStorage.getItem('demo-view-mode');
    return (saved as ViewMode) || 'corner';
  });
  
  // Refs for auto-scrolling
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Fetch widget configuration
  const fetchWidgetConfig = useCallback(async () => {
    if (!aiId || !collectionId) {
      setConfigLoaded(true);
      return;
    }

    try {
      // Use config for API base URL
      const baseUrl = config.apiBaseUrl;

      const response = await fetch(`${baseUrl}/api/widget/config/${aiId}/${collectionId}`);
      if (response.ok) {
        const config = await response.json();
        setWidgetConfig(config);
      } else {
        console.error('Failed to fetch widget config:', response.statusText);
        setWidgetConfig(null);
      }
    } catch (error) {
      console.error('Error fetching widget config:', error);
      setWidgetConfig(null);
    } finally {
      setConfigLoaded(true);
    }
  }, [aiId, collectionId]);

  // Load widget configuration on mount
  useEffect(() => {
    setConfigLoaded(false);
    fetchWidgetConfig();
  }, [fetchWidgetConfig]);

  // Check if Smartview is enabled when config loads
  useEffect(() => {
    if (widgetConfig && widgetConfig.instruction) {
      const hasSmartview = widgetConfig.instruction.toLowerCase().includes('smartview');
      setIsSmartviewEnabled(hasSmartview);
      if (hasSmartview) {
        console.log('🖼️ Smartview image upload feature enabled in Demo chat');
      }
    }
  }, [widgetConfig]);


  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current && chatContainerRef.current) {
      // For mobile, use more direct scrolling approach to avoid viewport jumping
      if (isMobile) {
        const container = chatContainerRef.current;
        const target = messagesEndRef.current;
        
        // Use requestAnimationFrame for smoother scrolling on mobile
        requestAnimationFrame(() => {
          container.scrollTo({
            top: target.offsetTop,
            behavior: 'smooth'
          });
        });
      } else {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [isMobile]);

  // Auto-scroll to bottom when new messages arrive with debounce for smoother mobile experience
  useEffect(() => {
    // Add a small delay for mobile to ensure DOM is updated and prevent scroll jumping
    const timeoutId = setTimeout(() => {
      scrollToBottom();
    }, isMobile ? 100 : 0);

    return () => clearTimeout(timeoutId);
  }, [messages, scrollToBottom, isMobile]);
  
  // Persist view mode preference (but not for mobile)
  useEffect(() => {
    if (!isMobile) {
      localStorage.setItem('demo-view-mode', viewMode);
    }
  }, [viewMode, isMobile]);

  // Force fullscreen on mobile
  useEffect(() => {
    if (isMobile && viewMode !== 'fullscreen') {
      setViewMode('fullscreen');
    }
  }, [isMobile, viewMode]);
  
  // Toggle view mode (disabled on mobile)
  const toggleViewMode = useCallback(() => {
    if (!isMobile) {
      setViewMode(prev => prev === 'fullscreen' ? 'corner' : 'fullscreen');
    }
  }, [isMobile]);

  // Handle mobile keyboard visibility for better UX
  useEffect(() => {
    if (!isMobile) return;

    const handleViewportChange = () => {
      // Auto-scroll to bottom when keyboard appears/disappears on mobile
      setTimeout(() => {
        scrollToBottom();
      }, 300); // Small delay to let keyboard animation finish
    };

    // Listen for viewport height changes (keyboard show/hide on mobile)
    const visualViewport = window.visualViewport;
    if (visualViewport) {
      visualViewport.addEventListener('resize', handleViewportChange);
      return () => {
        visualViewport.removeEventListener('resize', handleViewportChange);
      };
    }

    // Fallback for older browsers
    window.addEventListener('resize', handleViewportChange);
    return () => {
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [isMobile, scrollToBottom]);

  const handleSendMessage = useCallback(async () => {
    if (!aiId || !collectionId || !inputMessage.trim()) {
      setError('Please enter a message');
      return;
    }

    console.log('🚀 [Demo] Starting message send process');
    console.log('📝 [Demo] Message:', inputMessage.trim().substring(0, 50) + '...');
    console.log('🖼️ [Demo] Has image:', !!uploadedImageBase64);
    console.log('🔧 [Demo] Smartview enabled:', isSmartviewEnabled);
    console.log('💬 [Demo] Current conversationId:', conversationId);

    setLoading(true);
    setError(null);

    const messageToSend = inputMessage.trim();
    const imageToSend = uploadedImageBase64; // Capture image before clearing
    
    // Upload image first so backend marks uploaded_image (Smartview)
    let nextConversationId = conversationId;
    if (isSmartviewEnabled && imageToSend) {
      console.log('📤 [Demo] Uploading image to conversation...');
      try {
        const userIdentifier = `demo-${sessionId}`;
        const uploadRes = await uploadImageToConversation(
          aiId,
          collectionId,
          imageToSend,
          userIdentifier,
          conversationId
        );
        console.log('✅ [Demo] Image upload response:', uploadRes.data);
        
        if (uploadRes.data?.conversationId) {
          nextConversationId = uploadRes.data.conversationId;
          setConversationId(uploadRes.data.conversationId);
          console.log('🆔 [Demo] Updated conversationId:', nextConversationId);
        }
      } catch (err: any) {
        console.error('❌ [Demo] Error uploading image:', err);
        console.error('❌ [Demo] Error details:', err.response?.data || err.message);
        setError('Failed to upload image: ' + (err.response?.data?.error || err.message));
        setLoading(false);
        return; // Don't proceed if image upload fails
      }
    }

    // Now add user message after successful image upload (if any)
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      content: messageToSend,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage(''); // Clear input after adding message
    setUploadedImageBase64(null); // Clear uploaded image after adding message

    console.log('💬 [Demo] Sending chat request with conversationId:', nextConversationId);

    demoChat(aiId, collectionId, messageToSend, sessionId, nextConversationId)
      .then((res) => {
        console.log('✅ [Demo] Chat response received:', {
          hasResponse: !!res.data?.response,
          hasImage: !!res.data?.image,
          conversationId: res.data?.conversationId
        });
        
        if (res.data?.conversationId) {
          setConversationId(res.data.conversationId);
        }
        
        // Add AI response
        const aiMessage: ChatMessage = {
          id: `ai-${Date.now()}`,
          content: res.data.response,
          image: res.data.image,
          sender: 'ai',
          timestamp: new Date()
        };

        setMessages(prev => [...prev, aiMessage]);
        setLoading(false);
        
        if (res.data.image) {
          console.log('🖼️ [Demo] AI response includes image');
        }
      })
      .catch((err) => {
        console.error('❌ [Demo] Error sending message:', err);
        console.error('❌ [Demo] Error response:', err.response?.data);
        setError('Error: ' + (err.response?.data?.error || err.message));
        setLoading(false);
        
        // Remove the user message on error to avoid confusion
        setMessages(prev => prev.filter(msg => msg.id !== userMessage.id));
      });
  }, [aiId, collectionId, inputMessage, sessionId, uploadedImageBase64, conversationId, isSmartviewEnabled]);
  
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !loading) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [loading, handleSendMessage]);

  // Handle image upload
  const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('🖼️ [Demo] Image upload started');
    const file = e.target.files?.[0];
    if (!file) {
      console.log('⚠️ [Demo] No file selected');
      return;
    }

    console.log('📁 [Demo] File details:', {
      name: file.name,
      type: file.type,
      size: file.size,
      sizeKB: (file.size / 1024).toFixed(2) + ' KB'
    });

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      console.error('❌ [Demo] Invalid file type:', file.type);
      setError('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      console.error('❌ [Demo] File too large:', (file.size / 1024 / 1024).toFixed(2) + ' MB');
      setError('Image size must be less than 5MB');
      return;
    }

    console.log('✅ [Demo] File validation passed, reading file...');

    // Read the file and convert to base64
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      if (!base64 || !base64.startsWith('data:image/')) {
        console.error('❌ [Demo] Invalid base64 result');
        setError('Failed to process image file');
        return;
      }
      
      setUploadedImageBase64(base64);
      console.log('✅ [Demo] Image converted to base64:', {
        format: base64.substring(0, 30) + '...',
        length: base64.length
      });
    };
    reader.onerror = (error) => {
      console.error('❌ [Demo] FileReader error:', error);
      setError('Failed to read image file');
    };
    reader.readAsDataURL(file);

    // Clear the input so the same file can be re-uploaded
    e.target.value = '';
  }, []);
  
  
  // Component removed - will be defined outside the main component

  // Show loading while config is being fetched
  if (!configLoaded) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        bgcolor: '#fafafa'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: '3px solid #e0e0e0',
              borderTop: '3px solid #2196f3',
              animation: 'spin 1s linear infinite',
              mx: 'auto',
              mb: 2,
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          />
          <Typography color="text.secondary">Loading chat...</Typography>
        </Box>
      </Box>
    );
  }

  // Single return statement with conditional rendering to prevent component remounting
  return (
    <ErrorBoundary>
      {/* Corner Mode - Desktop Only */}
      {viewMode === 'corner' && !isMobile && (
        <CornerChatWindow
          viewMode={viewMode}
          error={error}
          messages={messages}
          inputMessage={inputMessage}
          loading={loading}
          chatContainerRef={chatContainerRef}
          messagesEndRef={messagesEndRef}
          toggleViewMode={toggleViewMode}
          setError={setError}
          setInputMessage={setInputMessage}
          handleSendMessage={handleSendMessage}
          handleKeyPress={handleKeyPress}
          handleImageUpload={handleImageUpload}
          isMobile={isMobile}
          isSmartviewEnabled={isSmartviewEnabled}
          uploadedImageBase64={uploadedImageBase64}
          widgetConfig={widgetConfig}
        />
      )}
      
      {/* Fullscreen Mode - Always on Mobile */}
      {(viewMode === 'fullscreen' || isMobile) && (
    <Box className="chat-container" sx={{
      height: isMobile ? 'calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))' : '100vh',
      maxHeight: isMobile ? '100dvh' : '100vh', // Fallback for older browsers
      display: 'flex',
      flexDirection: 'column',
      bgcolor: '#fafafa',
      // Mobile-specific optimizations
      ...(isMobile && {
        position: 'fixed',
        top: 'env(safe-area-inset-top, 0)', // Account for notch/status bar
        left: 'env(safe-area-inset-left, 0)',
        right: 'env(safe-area-inset-right, 0)',
        bottom: 'env(safe-area-inset-bottom, 0)', // Account for home indicator
        zIndex: 1300, // Higher z-index to ensure it's above other elements
        // Prevent bounce scrolling on iOS
        overscrollBehavior: 'none',
        // Optimize rendering
        transform: 'translateZ(0)',
        // Better mobile viewport handling
        minHeight: '0', // Allow shrinking when keyboard appears
      })
    }}>
      {/* Chat Header */}
      <Box className="chat-header" sx={{
        p: { xs: 1.5, sm: 2 },
        bgcolor: 'white',
        borderBottom: '1px solid #e0e0e0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box>
          <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 600, color: widgetConfig?.theme?.primaryColor || '#2196f3' }}>
            {widgetConfig?.aiTitle || 'AI Demo Chat'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Ask questions and explore this AI's capabilities
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isMobile && (
            <IconButton
              onClick={toggleViewMode}
              sx={{
                bgcolor: '#f0f8ff',
                color: '#1976d2',
                '&:hover': { bgcolor: '#e3f2fd' }
              }}
              title="Switch to corner mode"
            >
              <MinimizeIcon />
            </IconButton>
          )}
          {isMobile && (
            <IconButton
              onClick={() => window.history.back()}
              sx={{
                bgcolor: '#f0f8ff',
                color: '#1976d2',
                '&:hover': { bgcolor: '#e3f2fd' },
                minWidth: '40px',
                minHeight: '40px'
              }}
              title="Close chat"
            >
              ×
            </IconButton>
          )}
        </Box>
      </Box>
      
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Messages Area */}
      <Box
        ref={chatContainerRef}
        className="chat-messages"
        sx={{
          flex: 1,
          overflow: 'auto',
          overflowX: 'hidden', // Prevent horizontal scroll on mobile
          p: { xs: 1, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
          // Better mobile keyboard handling
          minHeight: 0, // Allow shrinking when keyboard appears
          // Mobile-specific optimizations
          ...(isMobile && {
            WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
            scrollBehavior: 'smooth',
            // Optimize for mobile performance
            transform: 'translateZ(0)', // Force hardware acceleration
            willChange: 'scroll-position',
            // Better keyboard handling
            paddingBottom: 'env(safe-area-inset-bottom, 0)',
            // Ensure content doesn't get cut off
            maxHeight: 'calc(100vh - 140px)', // Account for header and input area
          })
        }}
      >
        {(!messages || messages.length === 0) && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            textAlign: 'center'
          }}>
            <Box>
              <ChatBubbleOutlineIcon sx={{ fontSize: 60, color: '#bdbdbd', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Start a conversation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Send a message to begin chatting with the AI
              </Typography>
            </Box>
          </Box>
        )}
        
        {messages && messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            viewMode={viewMode}
            isMobile={isMobile}
            chatbotIcon={widgetConfig?.chatbotIcon || '🤖'}
            primaryColor={widgetConfig?.theme?.primaryColor || '#2196f3'}
            assistantName={widgetConfig?.assistantName || ''}
          />
        ))}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2, alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: (widgetConfig?.chatbotIcon || '').startsWith('data:image/') ? 'transparent' : (widgetConfig?.theme?.primaryColor || '#2196f3'), width: 32, height: 32 }}>
              {(widgetConfig?.chatbotIcon || '').startsWith('data:image/') ? (
                <img
                  src={widgetConfig?.chatbotIcon}
                  alt="Chatbot"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '50%'
                  }}
                />
              ) : (
                <span style={{ fontSize: '16px' }}>{widgetConfig?.chatbotIcon || '🤖'}</span>
              )}
            </Avatar>
            <Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: '18px 18px 18px 4px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      bgcolor: '#999',
                      borderRadius: '50%',
                      animation: 'typing-dots 1.4s infinite ease-in-out',
                      '&:nth-of-type(1)': { animationDelay: '0s' },
                      '@keyframes typing-dots': {
                        '0%, 80%, 100%': { transform: 'scale(0)' },
                        '40%': { transform: 'scale(1)' }
                      }
                    }}
                  />
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      bgcolor: '#999',
                      borderRadius: '50%',
                      animation: 'typing-dots 1.4s infinite ease-in-out',
                      animationDelay: '0.2s',
                      '@keyframes typing-dots': {
                        '0%, 80%, 100%': { transform: 'scale(0)' },
                        '40%': { transform: 'scale(1)' }
                      }
                    }}
                  />
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      bgcolor: '#999',
                      borderRadius: '50%',
                      animation: 'typing-dots 1.4s infinite ease-in-out',
                      animationDelay: '0.4s',
                      '@keyframes typing-dots': {
                        '0%, 80%, 100%': { transform: 'scale(0)' },
                        '40%': { transform: 'scale(1)' }
                      }
                    }}
                  />
                </Box>
              </Box>
            </Paper>
          </Box>
        )}
        
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box className="chat-input-area" sx={{
        p: { xs: 1, sm: 2 },
        bgcolor: 'white',
        borderTop: '1px solid #e0e0e0',
        boxShadow: '0 -1px 3px rgba(0,0,0,0.12)',
        // Better mobile safe area handling
        pb: { xs: `calc(8px + env(safe-area-inset-bottom, 0px))`, sm: 2 },
        // Mobile-specific optimizations
        ...(isMobile && {
          position: 'sticky',
          bottom: 0,
          zIndex: 100, // Higher z-index to stay above keyboard
          // Better keyboard handling
          minHeight: '70px',
          // Prevent input zoom on iOS and Android
          '& input, & textarea': {
            fontSize: '16px !important', // Prevent zoom on iOS
            lineHeight: '1.4 !important',
          },
          // Additional mobile keyboard handling
          transition: 'transform 0.3s ease-in-out',
          // Account for virtual keyboard
          '@supports (height: 100dvh)': {
            paddingBottom: 'max(env(safe-area-inset-bottom, 0px), env(keyboard-inset-height, 0px))',
          },
        })
      }}>
        <Box className="chat-input" sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          {isSmartviewEnabled && (
            <label htmlFor="fullscreen-image-upload" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <IconButton component="span" size={isMobile ? 'medium' : 'large'} sx={{ color: '#666', '&:hover': { bgcolor: '#f0f0f0' } }} title="Upload image">
                <span style={{ fontSize: isMobile ? '18px' : '20px' }}>📎</span>
              </IconButton>
              <input
                id="fullscreen-image-upload"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />
            </label>
          )}
          <TextField
            multiline
            maxRows={isMobile ? 3 : 4}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Type a message..."
            variant="outlined"
            fullWidth
            disabled={loading}
            onKeyPress={handleKeyPress}
            size={isMobile ? 'small' : 'medium'}
            inputProps={{
              ...(isMobile && {
                // Prevent zoom on focus for iOS
                style: { fontSize: '16px' },
                // Better mobile keyboard
                enterKeyHint: 'send',
                // Optimize input for mobile
                autoCapitalize: 'sentences',
                autoComplete: 'off',
                autoCorrect: 'on',
              })
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: { xs: '16px', sm: '20px' },
                bgcolor: '#f8f9fa',
                fontSize: { xs: '16px', sm: '16px' }, // Consistent 16px to prevent iOS zoom
                // Mobile touch improvements
                ...(isMobile && {
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                })
              }
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={loading || !inputMessage.trim()}
            size={isMobile ? 'medium' : 'large'}
            sx={{
              bgcolor: widgetConfig?.theme?.primaryColor || '#2196f3',
              color: 'white',
              minWidth: { xs: '44px', sm: '48px' },
              minHeight: { xs: '44px', sm: '48px' },
              '&:hover': {
                bgcolor: widgetConfig?.theme?.primaryColor || '#2196f3',
                filter: 'brightness(0.9)'
              },
              '&:disabled': {
                bgcolor: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            <SendIcon fontSize={isMobile ? 'small' : 'medium'} />
          </IconButton>
        </Box>
        {uploadedImageBase64 && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <img src={uploadedImageBase64} alt="Uploaded" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: '4px' }} />
            <Typography variant="caption">Image attached</Typography>
            <IconButton size="small" onClick={() => setUploadedImageBase64(null)} sx={{ ml: 'auto' }}>
              ×
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
      )}
    </ErrorBoundary>
  );
};

export default Demo;
